import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface ProductRatingProps {
  rating: number;
  reviewCount?: number;
  size?: "sm" | "md" | "lg";
  showCount?: boolean;
  className?: string;
}

export const ProductRating = ({
  rating,
  reviewCount = 0,
  size = "md",
  showCount = true,
  className
}: ProductRatingProps) => {
  const sizeClasses = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5"
  };

  const textSizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base"
  };

  const starSize = sizeClasses[size];
  const textSize = textSizeClasses[size];

  // Round to nearest 0.5
  const roundedRating = Math.round(rating * 2) / 2;

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= roundedRating) {
        // Full star
        stars.push(
          <Star key={i} className={cn(starSize, "fill-yellow-400 text-yellow-400")} />
        );
      } else if (i - 0.5 === roundedRating) {
        // Half star
        stars.push(
          <div key={i} className="relative">
            <Star className={cn(starSize, "text-gray-300")} />
            <div className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
              <Star className={cn(starSize, "fill-yellow-400 text-yellow-400")} />
            </div>
          </div>
        );
      } else {
        // Empty star
        stars.push(
          <Star key={i} className={cn(starSize, "text-gray-300")} />
        );
      }
    }
    return stars;
  };

  if (reviewCount === 0 && rating === 0) {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <div className="flex items-center">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className={cn(starSize, "text-gray-300")} />
          ))}
        </div>
        {showCount && (
          <span className={cn("text-muted-foreground", textSize)}>
            Aucun avis
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      <div className="flex items-center gap-0.5">
        {renderStars()}
      </div>
      <span className={cn("font-medium", textSize)}>
        {rating.toFixed(1)}
      </span>
      {showCount && reviewCount > 0 && (
        <span className={cn("text-muted-foreground", textSize)}>
          ({reviewCount})
        </span>
      )}
    </div>
  );
};
